<template>
    <div>
        <div class="position-relative overflow-hidden p-5 text-center">
            <b-container class="content">
                <div class="row">
                    <div class="col-md-6 title-box">
                        <h1 class="title-heading fw-bold text-primary">
                            Simple Invitations. <br />
                            Better Events.
                        </h1>
                        <p class="fw-bold sub-head pt-4 h6 text-secondary">Plan birthdays, baby showers, weddings,</p>
                        <p class="fw-bold sub-head h6 text-secondary">or just Friday night dinner—without the stress.</p>
                        <p class="fw-bold sub-head h6 text-secondary">dateit makes it easy to invite your people and get real RSVPs, fast.</p>
                        <b-button class="mt-3 btn-primary create-button" @click="createEvent">Create Event</b-button>
                        <div class="mt-3"><span class="text-secondary fw-bold">or</span> <a href="/docs/getting-started" class="fw-bold learn-more-link">Learn More →</a></div>
                    </div>
                    <div class="col-md-6">
                        <div v-if="isMobile" class="appstore-buttons:">
                            <a href="javascript:;" @click="iosAppLink" aria-label="Download dateit on App Store"><img width="168" height="50" class="app-badge mt-4 pe-2" src="/app-store-badge.svg" alt="Download on App Store" /></a>
                            <a href="javascript:;" @click="androidAppLink" aria-label="Download dateit on Google Play"><img width="168" height="50" class="app-badge mt-4" src="/google-play-badge.svg" alt="Get it on Google Play" /></a>
                        </div>
                        <div class="head-spacer">
                            <video
                                v-if="isDarkMode"
                                id="title-video"
                                class="iphone feature-image"
                                :class="{
                                    'slide-in': videoLoaded,
                                    'd-none': !videoLoaded,
                                }"
                                muted
                                autoplay
                                playsinline
                                aria-label="dateit app demo"
                            >
                                <source src="/video/home-dark.mp4" type="video/mp4" />
                            </video>
                            <video
                                v-else
                                id="title-video"
                                class="iphone feature-image"
                                :class="{
                                    'slide-in': videoLoaded,
                                    'd-none': !videoLoaded,
                                }"
                                muted
                                autoplay
                                playsinline
                                aria-label="dateit app demo"
                            >
                                <source src="/video/home-light.mp4" type="video/mp4" />
                            </video>
                        </div>
                        <div v-if="!isMobile" class="appstore-buttons">
                            <a href="javascript:;" @click="iosAppLink" aria-label="Download dateit on App Store"><img width="168" height="50" class="app-badge mt-4 pe-2" src="/app-store-badge.svg" alt="Download on App Store" /></a>
                            <a href="javascript:;" @click="androidAppLink" aria-label="Download dateit on Google Play"><img width="168" height="50" class="app-badge mt-4" src="/google-play-badge.svg" alt="Get it on Google Play" /></a>
                        </div>
                    </div>
                </div>
            </b-container>
        </div>

        <b-container class="content">
            <section class="my-5">
                <div class="d-flex justify-content-between align-items-center mx-2 my-5">
                    <h2 class="h3">What people are saying</h2>
                    <a class="h5 text-end" href="https://apps.apple.com/us/app/dateit/id1610780514?see-all=reviews">See All Reviews</a>
                </div>

                <div class="row my-4">
                    <div class="col-lg-4">
                        <review-box :title="'Finally an event planning app that isn\'t FB'">This app is shockingly simple to use and doesn't require any login and account creation. I was able to get an event created within a few minutes, I love how simple it is to use, and love that you can customize your event with a cover photo.</review-box>
                    </div>
                    <div class="col-lg-4">
                        <review-box :title="'Perfect replacement for Facebook events'">Perfect replacement for Facebook events'">The only reason I still have a Facebook is for the events, but this app totally replaces the need to use FB. Very good stuff.</review-box>
                    </div>
                    <div class="col-lg-4">
                        <review-box :title="'I love this app'">This app is great. It offers a lot more free features than the competition. I was able to add co-hosts and manage a birthday party with multiple people. I'll definitely be planning more events with dateit.</review-box>
                    </div>
                </div>
            </section>

            <hr class="featurette-divider" />

            <section class="features">
                <div class="row featurette">
                    <div class="col-md-7 order-md-2 text-start px-4">
                        <div class="col-md-8 float-end">
                            <h2 class="featurette-heading fw-bold">Plan it.</h2>
                            <p class="lead text-secondary">Create events that feel personal—not like spreadsheets.</p>
                            <ul class="text-secondary">
                                <li>Pick a cover photo or choose from our playful designs</li>
                                <li>Keep it private or share a link with anyone</li>
                                <li>Works for birthdays, bridal showers, family reunions, or anything worth gathering for</li>
                            </ul>
                        </div>
                    </div>
                    <div class="col-md-5 order-md-1 text-center">
                        <video v-if="isDarkMode" class="feature-image img-fluid mx-auto iphone" muted autoplay loop playsinline aria-label="Plan events with dateit">
                            <source src="/video/plan-it-dark.mp4" type="video/mp4" />
                        </video>
                        <video v-else class="feature-image img-fluid mx-auto iphone" muted autoplay loop playsinline aria-label="Plan events with dateit">
                            <source src="/video/plan-it-light.mp4" type="video/mp4" />
                        </video>
                    </div>
                </div>

                <hr class="featurette-divider" />

                <div class="row featurette">
                    <div class="col-md-7 text-start px-4">
                        <div class="col-md-8 text-start">
                            <h2 class="featurette-heading fw-bold">Share it.</h2>
                            <p class="lead text-secondary">Invite your friends and family with personal links, via SMS, or seamlessly from previous events.</p>
                            <ul class="text-secondary">
                                <li>Text blast your guests with a link to the event</li>
                                <li>Your guests can RSVP with one tap—no sign ups, no downloads</li>
                                <li>Invite as many people as you'd like (your guests don't pay a thing)</li>
                            </ul>
                        </div>
                    </div>
                    <div class="col-md-5 text-center">
                        <video v-if="isDarkMode" class="feature-image img-fluid mx-auto iphone" muted autoplay loop playsinline aria-label="Share events with dateit">
                            <source src="/video/invite-dark.mp4" type="video/mp4" />
                        </video>
                        <video v-else class="feature-image img-fluid mx-auto iphone" muted autoplay loop playsinline aria-label="Share events with dateit">
                            <source src="/video/invite-light.mp4" type="video/mp4" />
                        </video>
                    </div>
                </div>

                <hr class="featurette-divider" />

                <div class="row featurette">
                    <div class="col-md-7 order-md-2 text-start px-4">
                        <div class="col-md-8 float-end">
                            <h2 class="featurette-heading fw-bold">Discuss it.</h2>
                            <p class="lead text-secondary">Know who's bringing what—without a dozen group chats.</p>
                            <ul class="text-secondary">
                                <li>Guests can comment, coordinate, and ask questions right in the invite</li>
                                <li>Get updates instantly by text or notification</li>
                                <li>Keep everyone on the same page, without the back-and-forth</li>
                            </ul>
                        </div>
                    </div>
                    <div class="col-md-5 order-md-1 text-center">
                        <video v-if="isDarkMode" class="feature-image img-fluid mx-auto iphone" muted autoplay loop playsinline aria-label="Discuss events with dateit">
                            <source src="/video/discuss-it-dark.mp4" type="video/mp4" />
                        </video>
                        <video v-else class="feature-image img-fluid mx-auto iphone" muted autoplay loop playsinline aria-label="Discuss events with dateit">
                            <source src="/video/discuss-it-light.mp4" type="video/mp4" />
                        </video>
                    </div>
                </div>

                <hr class="featurette-divider" />

                <div class="row featurette">
                    <div class="col-md-7 text-start">
                        <div class="col-md-8 text-start">
                            <h2 class="featurette-heading fw-bold">Remember it.</h2>
                            <p class="lead text-secondary">Your event doesn't end when it ends.</p>
                            <ul class="text-secondary">
                                <li>Guests can upload and save photos in a shared album</li>
                                <li>Like, comment, and relive the moments later</li>
                                <li>Scroll through your memories feed anytime you want a smile</li>
                            </ul>
                        </div>
                    </div>
                    <div class="col-md-5 text-center">
                        <video v-if="isDarkMode" class="feature-image img-fluid mx-auto iphone" muted autoplay loop playsinline aria-label="Share photos with dateit">
                            <source src="/video/remember-it-dark.mp4" type="video/mp4" />
                        </video>
                        <video v-else class="feature-image img-fluid mx-auto iphone" muted autoplay loop playsinline aria-label="Share photos with dateit">
                            <source src="/video/remember-it-light.mp4" type="video/mp4" />
                        </video>
                    </div>
                </div>
            </section>

            <hr class="featurette-divider" />

            <section class="benefits py-5 my-5">
                <div class="row">
                    <div class="col-lg-4">
                        <h2>Real RSVPs. Real Easy.</h2>
                        <p class="text-secondary">From dinner parties to wedding weekends, date.it helps you get everyone there—with less chaos and more "yes." Invite your people. Enjoy your party. Let us handle the rest.</p>
                    </div>
                    <div class="col-lg-4">
                        <h2>Great events make great memories</h2>
                        <p class="text-secondary">Event planning should be fun. From backyard barbeques, baby showers, bridal showers, bachelor parties or even planning your wedding - the possibilities are endless and dateit makes it easy to plan anything.</p>
                    </div>
                    <div class="col-lg-4">
                        <h2>Invite anyone anywhere</h2>
                        <p class="text-secondary">Invite friends with personal links or within the app - iOS, Android, and the web. Invite directly from your contacts or share a link on social media.</p>
                    </div>
                </div>
            </section>
        </b-container>
    </div>
</template>

<script lang="ts" setup>
import GlobalRefs from "@/GlobalRefs";
import { mobileUtil } from "@/util/MobileUtil";
import { inject, onMounted, ref } from "vue";
import { getApp } from "firebase/app";
import { getAnalytics, logEvent } from "firebase/analytics";
import { useTheme } from "@/composables/useTheme";

const globalRefs = inject<GlobalRefs>("globalRefs")!;
const { isMobile } = mobileUtil();
const info = "";
const videoLoaded = ref(false);
const { isDarkMode } = useTheme();

onMounted(() => {
    var v = document.getElementById("title-video");
    if (v == null) {
        return;
    }
    v.addEventListener(
        "loadeddata",
        () => {
            videoLoaded.value = true;
        },
        false
    );
});

function createEvent() {
    // @ts-ignore
    globalRefs.createEditEventUi.showOnboardingUi();
}

function iosAppLink() {
    logEvent(getAnalytics(getApp()), "ios_conversion");
    window.open("https://apps.apple.com/us/app/dateit/id1610780514", "_blank");
}

function androidAppLink() {
    logEvent(getAnalytics(getApp()), "android_conversion");
    window.open("https://play.google.com/store/apps/details?id=io.dateit.app.android", "_blank");
}
</script>

<style scoped>
@keyframes slideInFromLeft {
    0% {
        transform: translateX(20%);
        opacity: 0;
    }

    100% {
        transform: translateX(0);
        opacity: 1;
    }
}

.iphone {
    border-radius: 14%/7%;
    background: var(--bg-solid);
    padding: 8px;
    box-shadow: 0 19px 38px rgba(0, 0, 0, 0.2), 0 16px 12px rgba(0, 0, 0, 0);
    border: 1px solid var(--border-color);
}

.slide-in {
    animation: 1.2s cubic-bezier(0.16, 0.6, 0.02, 0.93) 0s 1 slideInFromLeft;
}

.head-spacer {
    height: 540px;
}

.sub-head {
    color: #fff;
}

.feature-image {
    width: 250px;
    min-height: 490px;
    margin-top: 20px;
}

.featurette-divider {
    margin: 5rem 0;
    /* Space out the Bootstrap <hr> more */
}

ul {
    padding-left: 15px;
}

li {
    padding: 10px;
}

/* Thin out the marketing headings */
.featurette-heading {
    font-weight: 300;
    line-height: 1;
    letter-spacing: -0.05rem;
}

.title-heading {
    font-size: 25pt;
    margin-bottom: 0px;
    color: #fff;
}

.title-subheading {
    font-size: 30pt;
    color: #ccc;
}

.title-caption {
    color: #fff;
    padding-top: 10px;
}

.app-badge {
    height: 30px;
    width: auto;
}

.title-box {
    align-content: center;
}

.create-button {
    font-weight: bold;
    font-size: 14pt;
    padding: 10px;
    padding-left: 22px;
    padding-right: 22px;
}

.learn-more-link {
    text-decoration: none;
    transition: opacity 0.2s ease;
}

.learn-more-link:hover {
    opacity: 0.8;
    text-decoration: underline;
}

/* RESPONSIVE CSS
-------------------------------------------------- */

@media (min-width: 576px) {
    .iphone {
        padding: 8px;
    }

    .background-overlay {
        background-position-y: -180px;
    }

    .feature-image {
        width: 300px;
        min-height: 610px;
    }

    .head-spacer {
        height: 660px;
    }

    .app-badge {
        height: 50px;
        width: auto;
    }
}

@media (min-width: 768px) {
    .iphone {
        padding: 8px;
    }

    .background-overlay {
        background-position-y: -150px;
    }

    .title-heading {
        font-size: 40pt;
    }

    /* Bump up size of carousel content */
    .carousel-caption p {
        margin-bottom: 1.25rem;
        font-size: 1.25rem;
        line-height: 1.4;
    }

    .featurette-heading {
        margin-top: 12rem;
        font-size: 50px;
    }

    .title-box {
        margin-top: -100px;
        text-align: right;
    }

    .app-badge {
        height: 45px;
    }

    .title-heading {
        margin-top: 5rem;
    }

    .feature-image {
        width: 350px;
        min-height: 520px;
    }
    .head-spacer {
        height: 780px;
    }
}

@media (min-width: 992px) {
    .iphone {
        padding: 12px;
    }

    .background-overlay {
        background-position-y: -50px;
    }

    .featurette-heading {
        margin-top: 18rem;
    }

    .app-badge {
        height: 50px;
    }

    .feature-image {
        width: 400px;
        min-height: 770px;
    }
    .head-spacer {
        height: 880px;
    }
}

@media (min-width: 1200px) {
}
</style>
